/**
 * Подтверждение архивации полей из карточки хозяйства (AK-746).
 *
 * Отличие от общего экрана полей (AK-750): причина здесь — СПРАВОЧНИК, а не
 * свободная строка. Решение founder 25.08: именно по коду потом собирается
 * вкладка «Архив» и разбор отклонений сверки; свободный комментарий идёт
 * дополнением, а не вместо.
 */
import { useEffect, useState } from 'react';
import { Archive, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  ARCHIVE_REASON_CODES,
  ARCHIVE_REASON_LABELS,
  parseArchiveReason,
  type ArchiveReasonCode,
} from './archiveReason';
import type { SelectionStats } from './bulkActions';

const HA = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });

export function ArchiveFieldsDialog({
  open,
  stats,
  pending,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  stats: SelectionStats;
  pending: boolean;
  onCancel: () => void;
  /** Причина уже собрана в строку «код: комментарий». */
  onConfirm: (reason: string) => void;
}) {
  const [code, setCode] = useState<ArchiveReasonCode | null>(null);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Новый вызов — новый разговор: причина от прошлой пачки к этой не относится.
  useEffect(() => {
    if (open) {
      setCode(null);
      setComment('');
      setError(null);
    }
  }, [open]);

  const submit = () => {
    const parsed = parseArchiveReason(code, comment);
    if (!parsed.ok) {
      // Набранное НЕ затираем: человек видит, что написал, и правит.
      setError(parsed.error);
      return;
    }
    setError(null);
    onConfirm(parsed.value);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Архивировать {stats.count} {stats.count === 1 ? 'поле' : 'полей'}?
          </DialogTitle>
          <DialogDescription>
            {HA.format(stats.areaHa)} га. Поля исчезнут из активных списков, с карты
            и из планирования, но история посевов и операций сохранится.
            {stats.planned > 0 && (
              <>
                {' '}
                У {stats.planned} из них есть плановое занятие — оно уйдёт в архив
                вместе с полем.
              </>
            )}{' '}
            Действие обратимо: поле возвращается кнопкой «Восстановить» во вкладке
            «Архив».
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="farm-archive-reason">Причина архивации</Label>
            <Select
              value={code ?? undefined}
              onValueChange={(v) => {
                setCode(v as ArchiveReasonCode);
                // Правка снимает ошибку — иначе человек заперт в красном поле.
                if (error) setError(null);
              }}
            >
              <SelectTrigger
                id="farm-archive-reason"
                data-testid="farm-archive-reason"
                aria-invalid={error ? true : undefined}
                className={cn(error && 'border-destructive focus:ring-destructive')}
              >
                <SelectValue placeholder="Выберите причину" />
              </SelectTrigger>
              <SelectContent>
                {ARCHIVE_REASON_CODES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {ARCHIVE_REASON_LABELS[c]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {error ? (
              <span role="alert" className="text-xs text-destructive">
                {error}
              </span>
            ) : (
              <span className="text-xs text-muted-foreground">
                По ней потом видно, почему поля нет — и по ней же группируется архив.
              </span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="farm-archive-comment">Комментарий (необязательно)</Label>
            <Input
              id="farm-archive-comment"
              data-testid="farm-archive-comment"
              placeholder="например: сверка с «Историей поля» 25.08"
              value={comment}
              disabled={pending}
              onChange={(e) => setComment(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit();
              }}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={pending}>
            Отмена
          </Button>
          <Button
            variant="destructive"
            data-testid="farm-archive-confirm"
            onClick={submit}
            disabled={pending}
          >
            {pending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Архивирую…
              </>
            ) : (
              <>
                <Archive className="mr-2 h-4 w-4" />
                В архив
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
